import {FetchResult} from '@apollo/client';
import {recommendFieldsType} from '../CreateRecommend/recommendFields';
import type {newSubplanFields} from '../RecommendsSetUpNew/setUpNewFields';
import {PLAN_TYPE_DESTINATION} from '../CreateRecommend/RecommendSourceDestination/constants';
import FREQUENCY from '../../../constants/Frequency';
import OWNERSHIP from '../../../constants/Ownership';
import {OVRecommendation} from '../../../schema-types/OVRecommendation';
import {Pot} from '../../../schema-types/Pot';
import {
  UpsertOvrecommend,
  UpsertOvrecommendVariables,
} from '../../../schema-types/UpsertOvrecommend';
import validateUUID from '../../../utils/validateUUID';
import {getPlanFeesServerFromFormik} from '../CreateRecommend/PlanFees';

import {
  GenerateOvrecommendPots,
  GenerateOvrecommendPotsVariables,
} from '../../../schema-types/GenerateOvrecommendPots';
import {GenerateOVRPotBatchArguments} from '../../../schema-types/globalTypes';
import {SaltusAny} from '../../../utils/SaltusAny';
import {toUUID} from '../../../graphql/uuid';
import {AccountBasicFragment_contacts} from '../../../schema-types/AccountBasicFragment';

// ============================================
// COMPUTED FIELD HELPERS (not stored in state)
// ============================================

export const getIsMinor = (
  contact: string | undefined,
  contacts: AccountBasicFragment_contacts[]
): boolean => {
  if (!contact) {
    return false;
  }
  const foundContact = contacts.find((c) => c.id === contact);
  return Boolean(foundContact?.isMinor);
};

export const getAdviceWhat = (planAction: string | undefined): string => {
  const planActionsMap: Record<string, string> = {
    contribute: 'Contribute to',
    withdraw: 'Withdraw from',
    transfer: 'Transfer to',
    setUpNew: 'Set up a new',
    modify: 'Modify a',
    adHocFee: 'Charge an Ad-hoc Fee to',
    action: 'Action',
  };
  return planActionsMap[planAction || ''] || '';
};

export const getAdvicePlan = (
  mainPlanType: string | undefined,
  planTypes: Array<{value: string; label: string; capitalise?: boolean}>
): string => {
  if (!mainPlanType) {
    return '';
  }
  const plan = planTypes.find((p) => p.value === mainPlanType);
  if (!plan) {
    return '';
  }
  return plan.capitalise ? plan.label : plan.label.toLowerCase();
};

export type createSubplanProps = {
  subplanObj: newSubplanFields;
  childPotId?: string | null;
  reject?: () => void;
  accountId: string;
  reviewId: string;
  ovrecommendId: string;
  contactId: string | null;
  frequency: FREQUENCY;
  // ownership: string;
  isDummy: boolean;
};

export interface updateRecsVarsProps {
  accountId: string;
  values: recommendFieldsType;
  recommendDetails: OVRecommendation | null;
  ovrecommendId?: string;
  draft: boolean;
  generateRecommendationPots: (x: {
    variables: GenerateOvrecommendPotsVariables;
  }) => Promise<FetchResult<GenerateOvrecommendPots>>;
  upsertRecommendation: (x: {
    variables: UpsertOvrecommendVariables;
  }) => Promise<FetchResult<UpsertOvrecommend>>;
  enqueueSnackbar: (m: string, o: SaltusAny) => void;
  // optional
  goalId?: string;
  pots?: Pot[];
  contactName?: string;
  sourcePots?: Pot[];
  destinationPots?: Pot[];
  reviewId: string;
  contacts?: AccountBasicFragment_contacts[];
  // bankAccountProductNameId?: string | null;
}

export type updateRecommendPotsVariables = {
  ovrecommendId: string;
} & updateRecsVarsProps;

export function nonEmptyStringOrNull<T>(val?: T): T | null {
  if (typeof val === 'string' && val !== '') {
    return val;
  }
  if (typeof val === 'boolean') {
    return val;
  }
  // if (typeof val === 'number') {
  //   return val;
  // }
  return null;
}

export const getPotContactId = (contact: string | undefined, isMinor: boolean) => {
  if (contact === OWNERSHIP.TRUST) {
    return null;
  }
  if (contact === OWNERSHIP.JOINT_CODE && !isMinor) {
    return null;
  }
  return contact ? toUUID(contact) : null;
};

export const getPromiseDelete = async ({
  recommendDetails,
  deleteRecommendationPots: deleteRecommendationPot,
}: SaltusAny): Promise<any> => {
  // preventing the application from hanging
};

export const createOrUpdateSubplan = ({
  subplanObj,
  reviewId,
  ovrecommendId,
  isDummy,
  // ownership,
  contactId,
  frequency,
  childPotId,
}: createSubplanProps): GenerateOVRPotBatchArguments => {
  // let potId = childPotId || '';
  // TODO: what kind of fee
  const fees = getPlanFeesServerFromFormik(subplanObj.planFees);

  const potVariables: GenerateOVRPotBatchArguments = {
    isDummy,
    reviewId: childPotId ? undefined : toUUID(reviewId),
    mandate: typeof subplanObj?.model === 'string' ? subplanObj?.model : null,
    modelPortfolioId:
      typeof subplanObj?.model === 'string'
        ? null
        : validateUUID(subplanObj?.model?.id || ''),
    productProviderId: subplanObj?.planProvider,
    isSubplan: true,
    contactId: contactId ? toUUID(contactId) : null,
    // ownership,
    fees,
    hasFeeDeviation: subplanObj.planFees?.feeDeviation || false,
    originalOvrId: childPotId ? undefined : toUUID(ovrecommendId),
    potId: childPotId ? toUUID(childPotId) : null,
    planType: PLAN_TYPE_DESTINATION,
    singleClientAmount: Number(subplanObj?.singleClientAmount || 0),
    singleEmployerAmount: Number(subplanObj?.singleEmployerAmount || 0),
    recurringClientAmount: Number(subplanObj?.recurringClientAmount || 0),
    recurringEmployerAmount: Number(subplanObj?.recurringEmployerAmount || 0),
    recurringEmployerFrequency: subplanObj?.recurringEmployerAmount // if no employer amount, dont provide frequency
      ? frequency
      : undefined,
    recurringClientFrequency: frequency,
  };
  return potVariables;
};
